import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import CarLeft from "@/assets/illustrations/CarLeft.png";
import CarRight from "@/assets/illustrations/CarRight.png";
import { RecentHistoryTable } from "@/components/custom/recentsTable";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import CheckInService from "@/services/api/CheckInService";
import { recentHistoryObject } from "@/central-atoms/everest-fleet-pwa/types";
import { useNavigate } from "react-router-dom";
import { BottomNav } from "@/components/custom/BottomNav";
import { useHubDetails, useSupervisorDetails } from "@/redux/hooks/useSupervisorDetails";
import { useDispatch } from "react-redux";
import { setCheckInDetails } from "@/redux/checkInDetailsSlice";
import ErrorMessageFullCard from "@/components/custom/ErrorMessageFullCard";
import CheckInDriverService from "@/services/api/CheckInDriverService";
import { CheckInRecord } from "@/central-atoms/everest-fleet-pwa/responses";
import DriverCheckInStack from "@/components/custom/DriverCheckInStack";
import ErrorSnackbar from "@/components/custom/ErrorSnackbar";

const CheckIn = () => {
  const [recentHistory, setRecentHistory] = useState<recentHistoryObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [selectedOption, setSelectedOption] = useState("et_id");
  const [errorMessage, setErrorMessage] = useState<string[]>([]);
  const [checkInError, setCheckInError] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const navigate = useNavigate();
  const hubDetails = useHubDetails();
  const supervisorDetails = useSupervisorDetails();
  const dispatch = useDispatch();

  // everest_checkin_apis: an ET ID can no longer be resolved to a driver, so check-in is by
  // login token only and the ET ID option is hidden.
  const etIdCheckInDisabled = supervisorDetails?.data?.et_id_check_in_disabled === true;

  useEffect(() => {
    if (etIdCheckInDisabled) {
      setSelectedOption("token_number");
    }
  }, [etIdCheckInDisabled]);

  const [checkInRecords, setCheckInRecords] = useState<CheckInRecord[]>([]);

  // Fetch check-in requests on mount
  useEffect(() => {
    const fetchCheckIns = async () => {
      setLoading(true);
      try {
        const response = await CheckInDriverService.getCheckInRequests();
        const records = response.data.records;
        setCheckInRecords(records);
        setCurrentIndex(0);
      } catch (err) {
        setCheckInError(["Failed to load check-in requests"]);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckIns();
  }, []);



  useEffect(() => {
    const fetchCheckInHistory = async () => {
      try {
        const response = await CheckInService.getCheckInHistory(
          hubDetails?.name as string,
          1,
          5
        );
        const records = response.data.data.records;

        // Map API response to match recentHistoryObject structure
        const formattedHistory = records.map((record) => ({
          carNumber: record.track_props.car_number as string,
          dateTime: new Date(record.created_on).toLocaleString(),
        }));

        setRecentHistory(formattedHistory as any);
      } catch (error) {
        console.error("Failed to fetch check-in history", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCheckInHistory();
  }, [hubDetails?.name]);

  const handleConfirmClick = async () => {
    try {
      setErrorMessage([]); // Clear any previous error messages

      const reqBody: any = {
        hub: hubDetails?.name as string,
      };

      if (selectedOption === "et_id") {
        reqBody.et_id = inputValue;
      } else {
        reqBody.token = inputValue;
      }

      const response = await CheckInService.getCheckInDetails(reqBody);

      if (response.success) {
        const checkInDetails = {
          vehicleNumber: response.data.details.vrn,
          pilotName: response.data.details.lastAllotedTo,
          timeSinceAllocated: response.data.details.lastAllotedOn,
          odometer: response.data.details.odo,
          soc: response.data.details.soc,
          canValidityScore: response.data.details.successRate?.toString(),
          efficiencyScore: response.driver_efficiency.toString(),
          token: selectedOption === "token_number" ? inputValue : undefined,
          et_id: selectedOption === "et_id" ? inputValue : undefined,
        };
        dispatch(setCheckInDetails(checkInDetails));
        navigate("/check-in/car-allocation");
      } else {
        console.error("Failed to fetch check-in details", response);
        if (response?.data?.messages?.length) {
          setErrorMessage(response?.data?.messages);
        }
      }
    } catch (error: any) {
      console.error("Failed to fetch check-in details", error);
      if (error?.response?.data?.data?.messages?.length) {
        setErrorMessage(error.response.data.data.messages);
      }
    }
  };

  if (checkInRecords.length > 0) {
    return <DriverCheckInStack checkInRecords={checkInRecords} setCheckInError={setCheckInError} checkInError={checkInError} setCheckInRecords={setCheckInRecords} currentIndex={currentIndex} setCurrentIndex={setCurrentIndex}/>;
  }

  return (
    <>
      {errorMessage.length > 0 && (
        <ErrorMessageFullCard
          errorString={errorMessage}
          handleBackToHome={() => {
            setErrorMessage([]);
            navigate("/check-in");
          }}
        >
          <div className="pb-[10px]">
            {selectedOption === "et_id" ? (
              <div className="text-secondary font-bold pb-[4px] text-[14px]">
                Driver Id: {inputValue}
              </div>
            ) : (
              <div className="text-secondary font-bold pb-[4px] text-[14px]">
                Token: {inputValue}
              </div>
            )}
          </div>
        </ErrorMessageFullCard>
      )}
      <div className="bg-secondary min-h-screen flex flex-col items-center gap-y-5">
        <div className="w-full flex justify-start mt-10">
          <img src={CarLeft} className="w-1/2" />
        </div>

        {etIdCheckInDisabled ? (
          <h1 className="text-secondary-foreground text-center mt-2">
            Enter Token Number
          </h1>
        ) : (
          <RadioGroup
            defaultValue="et_id"
            className="w-5/6 flex flex-row justify-between mt-2 px-2 items-center"
            onValueChange={(value) => setSelectedOption(value)}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="token_number" id="token_number" />
              <Label htmlFor="token_number" className="text-primary-foreground">
                Token Number
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="et_id" id="et_id" />
              <Label htmlFor="et_id" className="text-primary-foreground">
                ET ID
              </Label>
            </div>
          </RadioGroup>
        )}

        {/* Wrapper for input and button */}
        <div className="w-5/6 flex flex-col gap-4">
          <Input
            variant="primary"
            className="text-secondary-foreground w-full border-4"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={handleConfirmClick}
          >
            Confirm
          </Button>
        </div>

        {errorMessage && (
          <p className="text-red-500 w-5/6 text-left">{errorMessage}</p>
        )}

        <div className="w-5/6 rounded-2xl bg-[#0a4762]">
          <h3 className="flex flex-row justify-start text-white px-4 pt-3">
            Recent History
          </h3>
          {loading ? (
            <p className="text-white px-4">Loading...</p>
          ) : (
            <RecentHistoryTable data={recentHistory} />
          )}
        </div>
      </div>
      {
        checkInError?.length > 0 && <ErrorSnackbar message={checkInError[0]} />
      }
      <BottomNav active={2} />
    </>
  );
};

export default CheckIn;
